import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useLang, waLink } from "@/i18n";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldCls =
  "rounded-xl border-slate-200 focus-visible:ring-blue-600 focus-visible:border-blue-600";

const Field = ({ label, optional, children, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <Label className="text-sm font-medium text-slate-700">
      {label} {optional && <span className="text-xs font-normal text-slate-400">({optional})</span>}
    </Label>
    {children}
  </div>
);

export const CustomerRequestDialog = ({ open, onOpenChange, defaultService }) => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", service: "", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  React.useEffect(() => {
    if (defaultService) setForm((f) => ({ ...f, service: defaultService }));
  }, [defaultService, open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.city || !form.service) {
      toast.error(t.forms.required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/customer`, form);
      toast.success(t.forms.successCustomer);
      onOpenChange(false);
      setForm({ name: "", phone: "", email: "", city: "", service: "", message: "" });
    } catch {
      toast.error(t.forms.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="customer-request-dialog" className="max-h-[90vh] overflow-y-auto rounded-3xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{t.forms.customerTitle}</DialogTitle>
          <DialogDescription className="text-slate-500">{t.hero.subtitle}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-4">
          <Field label={t.forms.name}>
            <Input data-testid="customer-name-input" value={form.name} onChange={set("name")} className={fieldCls} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t.forms.phone}>
              <Input data-testid="customer-phone-input" value={form.phone} onChange={set("phone")} className={fieldCls} />
            </Field>
            <Field label={t.forms.city}>
              <Input data-testid="customer-city-input" value={form.city} onChange={set("city")} className={fieldCls} />
            </Field>
          </div>
          <Field label={t.forms.email} optional={t.forms.optional}>
            <Input data-testid="customer-email-input" type="email" value={form.email} onChange={set("email")} className={fieldCls} />
          </Field>
          <Field label={t.forms.service}>
            <select
              data-testid="customer-service-select"
              value={form.service}
              onChange={set("service")}
              className="flex h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
            >
              <option value="">{t.forms.selectService}</option>
              {t.services.items.map((s, i) => (
                <option key={i} value={s.t}>{s.t}</option>
              ))}
            </select>
          </Field>
          <Field label={t.forms.message} optional={t.forms.optional}>
            <Textarea data-testid="customer-message-input" rows={3} value={form.message} onChange={set("message")} className={fieldCls} />
          </Field>
          <Button
            data-testid="customer-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-blue-600 py-6 text-base font-semibold text-white hover:bg-blue-700"
          >
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t.forms.sending}</> : t.forms.submitCustomer}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const CleanerForm = () => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", city: "", experience: "", availability: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.city || !form.experience || !form.availability) {
      toast.error(t.forms.required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/cleaner`, form);
      toast.success(t.forms.successCleaner);
      setForm({ name: "", phone: "", city: "", experience: "", availability: "" });
    } catch {
      toast.error(t.forms.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="cleaner-form" onSubmit={submit} className="space-y-4 rounded-3xl bg-white p-6 shadow-xl sm:p-8">
      <h3 className="font-heading text-2xl font-semibold text-slate-900">{t.forms.cleanerTitle}</h3>
      <Field label={t.forms.name}>
        <Input data-testid="cleaner-name-input" value={form.name} onChange={set("name")} className={fieldCls} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t.forms.phone}>
          <Input data-testid="cleaner-phone-input" value={form.phone} onChange={set("phone")} className={fieldCls} />
        </Field>
        <Field label={t.forms.city}>
          <Input data-testid="cleaner-city-input" value={form.city} onChange={set("city")} className={fieldCls} />
        </Field>
      </div>
      <Field label={t.forms.experience}>
        <Textarea data-testid="cleaner-experience-input" rows={2} value={form.experience} onChange={set("experience")} className={fieldCls} />
      </Field>
      <Field label={t.forms.availability}>
        <Input data-testid="cleaner-availability-input" value={form.availability} onChange={set("availability")} className={fieldCls} />
      </Field>
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          data-testid="cleaner-submit-btn"
          type="submit"
          disabled={loading}
          className="flex-1 rounded-full bg-blue-600 py-6 text-base font-semibold text-white hover:bg-blue-700"
        >
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t.forms.sending}</> : t.become.cta1}
        </Button>
        <a
          data-testid="cleaner-whatsapp-btn"
          href={waLink("Hoi MyCleaner, ik wil me aanmelden als schoonmaker.")}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-1 items-center justify-center rounded-full border-2 border-blue-600 py-3.5 text-base font-semibold text-blue-700 hover:bg-blue-50"
        >
          {t.become.cta2}
        </a>
      </div>
    </form>
  );
};

export const ContactForm = () => {
  const { t } = useLang();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error(t.forms.required);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/contact`, form);
      toast.success(t.forms.successContact);
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error(t.forms.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-4 rounded-3xl border border-slate-100 bg-white p-6 shadow-lg sm:p-8">
      <Field label={t.forms.name}>
        <Input data-testid="contact-name-input" value={form.name} onChange={set("name")} className={fieldCls} />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t.forms.phone} optional={t.forms.optional}>
          <Input data-testid="contact-phone-input" value={form.phone} onChange={set("phone")} className={fieldCls} />
        </Field>
        <Field label={t.forms.email} optional={t.forms.optional}>
          <Input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} className={fieldCls} />
        </Field>
      </div>
      <Field label={t.forms.message}>
        <Textarea data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} className={fieldCls} />
      </Field>
      <Button
        data-testid="contact-submit-btn"
        type="submit"
        disabled={loading}
        className="w-full rounded-full bg-blue-600 py-6 text-base font-semibold text-white hover:bg-blue-700"
      >
        {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t.forms.sending}</> : t.forms.submitContact}
      </Button>
    </form>
  );
};
